import { generateUrl } from '@nextcloud/router';
import { relative } from 'path';
import queryString from 'query-string';
import Router, { isNavigationFailure, NavigationFailureType } from 'vue-router';
import Vue from 'vue';
import { useFilesStore } from '../store/files.ts';
import { usePathsStore } from '../store/paths.ts';
import { defaultView } from '../utils/filesViews.ts';
import logger from '../logger.ts';
Vue.use(Router);
// Prevent router from throwing errors when we're already on the page we're trying to go to
const originalPush = Router.prototype.push;
Router.prototype.push = (function (...args) {
    if (args.length > 1) {
        return originalPush.call(this, ...args);
    }
    return originalPush.call(this, args[0]).catch(ignoreDuplicateNavigation);
});
const originalReplace = Router.prototype.replace;
Router.prototype.replace = (function (...args) {
    if (args.length > 1) {
        return originalReplace.call(this, ...args);
    }
    return originalReplace.call(this, args[0]).catch(ignoreDuplicateNavigation);
});
/**
 * Ignore duplicated-navigation error but forward real exceptions
 * @param error The thrown error
 */
function ignoreDuplicateNavigation(error) {
    if (isNavigationFailure(error, NavigationFailureType.duplicated)) {
        logger.debug('Ignoring duplicated navigation from vue-router', { error });
    }
    else {
        throw error;
    }
}
const router = new Router({
    mode: 'history',
    // if index.php is in the url AND we got this far, then it's working:
    // let's keep using index.php in the url
    base: generateUrl('/apps/files'),
    linkActiveClass: 'active',
    routes: [
        {
            path: '/',
            // Pretending we're using the default view
            redirect: { name: 'filelist', params: { view: defaultView() } },
        },
        {
            path: '/:view/:fileid(\\d+)?',
            name: 'filelist',
            props: true,
        },
    ],
    // Custom stringifyQuery to prevent encoding of slashes in the url
    stringifyQuery(query) {
        const result = queryString.stringify(query).replace(/%2F/gmi, '/');
        return result ? ('?' + result) : '';
    },
});
// Handle aborted navigation (NavigationGuards) gracefully
router.onError((error) => {
    if (isNavigationFailure(error, NavigationFailureType.aborted)) {
        logger.debug('Navigation was aboorted', { error });
    }
    else {
        throw error;
    }
});
// If navigating back from a folder to a parent folder,
// we need to keep the current dir fileid so it's highlighted
// and scrolled into view.
router.beforeResolve((to, from, next) => {
    if (to.params?.parentIntercept) {
        delete to.params.parentIntercept;
        return next();
    }
    if (to.params.view !== from.params.view) {
        // skip if different views
        return next();
    }
    const fromDir = (from.query?.dir || '/');
    const toDir = (to.query?.dir || '/');
    // We are going back to a parent directory
    if (relative(fromDir, toDir) === '..') {
        const { getNode } = useFilesStore();
        const { getPath } = usePathsStore();
        if (!from.params.view) {
            logger.error('No current view id found, cannot navigate to parent directory', { fromDir, toDir });
            return next();
        }
        // Get the previous parent's file id
        const fromSource = getPath(from.params.view, fromDir);
        if (!fromSource) {
            logger.error('No source found for the parent directory', { fromDir, toDir });
            return next();
        }
        const fileId = getNode(fromSource)?.fileid;
        if (!fileId) {
            logger.error('No fileid found for the parent directory', { fromDir, toDir, fromSource });
            return next();
        }
        logger.debug('Navigating back to parent directory', { fromDir, toDir, fileId });
        return next({
            name: 'filelist',
            query: to.query,
            params: {
                ...to.params,
                fileid: String(fileId),
                // Prevents the beforeEach from being called again
                parentIntercept: 'true',
            },
            // Replace the current history entry
            replace: true,
        });
    }
    // else, we just continue
    next();
});
export default router;
